import type { ComponentPropsWithoutRef, HTMLProps } from 'react';
import React, { useState } from 'react';
import { GeologyType } from '~/apollo/generated/v4/graphql';
import { Heading } from '~/components/common/Heading';
import { Tooltip } from '~/components/common/Tooltip';
import { GeologyTypeIconV4 } from '~/components/common/icons/geology-type-icon-v4';
import { KPPanelContextProvier } from '~/components/key-parameters/key-parameters-panel-v4/kp-panel-context';
import { cn } from '~/utils/common';
import type { OCAdditionalFields } from '~/utils/modules/outcrop-v4';
import { readableGqlEnum } from '~/utils/text';
import { GeologyTypeTab } from './geology-type-tab';

function KPTitleWrapper({ className, ...props }: HTMLProps<HTMLSpanElement>) {
  return (
    <span
      className={cn('font-bold cursor-default break-words', className)}
      {...props}
    />
  );
}

type KPTitleProps = { name: string; abbreviation?: string };
export function KPTitle({ name, abbreviation }: KPTitleProps) {
  if (!abbreviation) {
    return (
      <KPTitleWrapper>
        {/* <wbr> is "word break opportunity", so this will allow for automatic
        line breaks in the label if needed */}
        <span
          dangerouslySetInnerHTML={{ __html: name.replace('/', '/<wbr>') }}
        />
      </KPTitleWrapper>
    );
  }

  return (
    <Tooltip message={name}>
      <KPTitleWrapper>{abbreviation}</KPTitleWrapper>
    </Tooltip>
  );
}

type TabButtonContainerProps = ComponentPropsWithoutRef<'div'> & {
  active: boolean;
};
export function TabButtonContainer({
  active,
  className,
  ...props
}: TabButtonContainerProps) {
  return (
    <div
      className={cn('inline-block p-0.5 rounded-t-md', {
        'bg-white border border-slate-300 border-b-white': active,
        'opacity-50 hover:opacity-100': !active,
      })}
      {...props}
    />
  );
}

type TabButtonProps = ComponentPropsWithoutRef<'button'> & {
  active: boolean;
};
export function TabButton({
  active,
  className,
  children,
  ...props
}: TabButtonProps) {
  return (
    <button
      type="button"
      className="p-1 rounded-md flex items-center justify-center bg-transparent"
      {...props}
    >
      {children}
    </button>
  );
}

// Proper key parameters have a unique ID and non-nullable values;
// AE key parameters may not have values defined
type KeyParameters = {
  id: string;
  geologyType?: string | null;
  grossDepositionalEnvironment?: string | null;
  depositionalEnvironment?: string | null;
  depositionalSubEnvironment?: string | null;
  architecturalElement?: string | null;
  dominant?: boolean;
};

export type KeyParametersPanelProps = {
  keyParameters: KeyParameters[];
  dominantGeologyType?: GeologyType | null;
  outcrop?: OCAdditionalFields;
  noHeading?: boolean;
  leftColMd?: number;
  rightColMd?: number;
  children?: (kps: React.ReactNode, tabs: React.ReactNode) => JSX.Element;
  view?: 'table' | 'combined';
  forceShow?: boolean;
};

export function KeyParametersPanelV4({
  keyParameters,
  dominantGeologyType,
  outcrop,
  noHeading: headingDisabled = false,
  leftColMd = 4,
  rightColMd = 8,
  view,
  children,
  forceShow,
}: KeyParametersPanelProps) {
  // Create a map of geology types to their key parameters
  const kpPanelTabs = (() => {
    return Object.values(GeologyType).reduce((acc, gt) => {
      acc.set(
        gt,
        keyParameters.filter(kp => kp.geologyType === gt),
      );
      return acc;
    }, new Map<GeologyType, KeyParameters[]>());
  })();

  // Show tab bar as long as there's at least one KP
  const showTabs = keyParameters.length > 0;

  const firstVisibleTab = ((): GeologyType => {
    if (dominantGeologyType && kpPanelTabs.get(dominantGeologyType)?.length) {
      return dominantGeologyType;
    }
    for (const gt of Object.values(GeologyType)) {
      if (kpPanelTabs.get(gt)?.length) {
        return gt;
      }
    }
    return GeologyType.Clastic;
  })();

  const [currentTab, setCurrentTab] = useState(firstVisibleTab);

  if (!keyParameters.length && !forceShow) return null;

  const tabButtons = showTabs ? (
    <div className="space-x-1 bg-slate-100 rounded-t-md mr-1">
      {Array.from(kpPanelTabs.keys()).map(gt => {
        const kps = kpPanelTabs.get(gt) ?? [];
        const dominant = dominantGeologyType === gt;
        if (!kps.length) return null;
        return (
          <Tooltip
            key={gt}
            message={`${readableGqlEnum(gt)}${dominant ? ' (dominant)' : ''}`}
            position="top"
          >
            <TabButtonContainer active={gt === currentTab}>
              <TabButton
                active={gt === currentTab}
                onClick={() => setCurrentTab(gt)}
              >
                <GeologyTypeIconV4
                  geologyType={gt}
                  dominant={dominant}
                  hideTooltip
                  className={cn('h-6 w-6', {
                    'b-orange-500 border-2': dominant,
                  })}
                />
              </TabButton>
            </TabButtonContainer>
          </Tooltip>
        );
      })}
    </div>
  ) : null;

  const kpContent = (
    <GeologyTypeTab
      geologyType={currentTab}
      keyParameters={kpPanelTabs.get(currentTab) ?? []}
      outcrop={outcrop}
      leftColMd={leftColMd}
      rightColMd={rightColMd}
      view={view}
    />
  );

  return (
    <KPPanelContextProvier>
      {children ? (
        children(kpContent, tabButtons)
      ) : (
        <>
          <div className="float-right">{showTabs && tabButtons}</div>
          {!headingDisabled && <Heading level={3}>Key Parameters</Heading>}
          <div className="clear-both" />
          {kpContent}
        </>
      )}
    </KPPanelContextProvier>
  );
}
